import { BankAccount, Contract, PlanfactOperation } from '../services/api';
import { sumContractReceiptsFromOperations } from './contractReceipts';

export type ContractReceiptsSummaryKind = 'remainder' | 'debt' | 'paid';

export interface ContractReceiptsTableRow {
  key: string;
  contractId: number | null;
  contractNumber: string;
  hierarchyLevel: number | null;
  bankAccountId: number | null;
  planfactAccount: string;
  currencyType: string;
  totalAmount: number;
  paidAmount: number;
  remainder: number;
  paidPercent: number;
  isTotal: boolean;
  isCurrent: boolean;
}

export interface ContractReceiptsBarRow {
  key: string;
  paidPercent: number;
  isTotal: boolean;
  isCurrent: boolean;
}

export interface ContractReceiptsTabData {
  summaryKind: ContractReceiptsSummaryKind;
  summaryAmount: number | null;
  summaryLabel: string;
  tableRows: ContractReceiptsTableRow[];
  barRows: ContractReceiptsBarRow[];
  totals: {
    totalAmount: number;
    paidAmount: number;
    remainder: number;
    paidPercent: number;
  };
}

function resolvePaidAmount(
  contractId: number,
  planfactOperations: PlanfactOperation[],
  fallbackPaid: number | null | undefined,
): number {
  if (planfactOperations.length > 0) {
    return sumContractReceiptsFromOperations(planfactOperations, contractId);
  }
  return Number(fallbackPaid) || 0;
}

function resolveCurrencyType(
  bankAccountId: number | null,
  storedCurrency: string | null | undefined,
  bankAccountsById: Map<number, BankAccount>,
): string {
  if (bankAccountId != null) {
    return bankAccountsById.get(bankAccountId)?.currency_type ?? storedCurrency ?? '—';
  }
  return storedCurrency?.trim() ? storedCurrency : '—';
}

function resolvePlanfactAccount(
  bankAccountId: number | null,
  storedAccount: string | null | undefined,
  bankAccountsById: Map<number, BankAccount>,
): string {
  if (bankAccountId != null) {
    return bankAccountsById.get(bankAccountId)?.planfact_account ?? storedAccount ?? '—';
  }
  return storedAccount?.trim() ? storedAccount : '—';
}

export function buildContractReceiptsTabData(params: {
  familyContracts: Contract[];
  planfactOperations: PlanfactOperation[];
  bankAccounts: BankAccount[];
  bankAccountDrafts: Record<number, number | null>;
  amountDrafts?: Record<number, number | null>;
  currentContractId?: number;
  displayStatus: string;
  familyFallbackPaid?: number;
}): ContractReceiptsTabData {
  const {
    familyContracts,
    planfactOperations,
    bankAccounts,
    bankAccountDrafts,
    amountDrafts,
    currentContractId,
    displayStatus,
    familyFallbackPaid,
  } = params;

  const bankAccountsById = new Map(bankAccounts.map((account) => [account.id, account]));
  const hasPerContractOps = planfactOperations.length > 0;

  const contractRows = familyContracts.map((ct) => {
    const bankAccountId = bankAccountDrafts[ct.id] ?? ct.bank_account_id ?? null;
    const draftAmount = amountDrafts?.[ct.id];
    const totalAmount =
      draftAmount != null && Number.isFinite(draftAmount)
        ? draftAmount
        : Number(ct.total_amount) || 0;
    const paidAmount = hasPerContractOps
      ? resolvePaidAmount(ct.id, planfactOperations, undefined)
      : 0;
    const remainder = totalAmount - paidAmount;
    const paidPercent = totalAmount > 0 ? Math.round((paidAmount / totalAmount) * 100) : 0;

    return {
      key: `contract-${ct.id}`,
      contractId: ct.id,
      contractNumber: ct.contract_number,
      hierarchyLevel: ct.hierarchy_level ?? 1,
      bankAccountId,
      planfactAccount: resolvePlanfactAccount(bankAccountId, ct.planfact_account, bankAccountsById),
      currencyType: resolveCurrencyType(bankAccountId, ct.currency_type, bankAccountsById),
      totalAmount,
      paidAmount,
      remainder,
      paidPercent,
      isTotal: false,
      isCurrent: ct.id === currentContractId,
    };
  });

  const totals = contractRows.reduce(
    (acc, row) => ({
      totalAmount: acc.totalAmount + row.totalAmount,
      paidAmount: acc.paidAmount + row.paidAmount,
      remainder: acc.remainder + row.remainder,
    }),
    { totalAmount: 0, paidAmount: 0, remainder: 0 },
  );

  if (!hasPerContractOps && familyFallbackPaid != null) {
    totals.paidAmount = Number(familyFallbackPaid) || 0;
    totals.remainder = totals.totalAmount - totals.paidAmount;
  }

  const paidPercent =
    totals.totalAmount > 0 ? Math.round((totals.paidAmount / totals.totalAmount) * 100) : 0;

  const totalRow: ContractReceiptsTableRow = {
    key: 'total',
    contractId: null,
    contractNumber: 'Итого',
    hierarchyLevel: null,
    bankAccountId: null,
    planfactAccount: '—',
    currencyType: '—',
    totalAmount: totals.totalAmount,
    paidAmount: totals.paidAmount,
    remainder: totals.remainder,
    paidPercent,
    isTotal: true,
    isCurrent: false,
  };

  const normalizedStatus = displayStatus.trim().toLowerCase();
  let summaryKind: ContractReceiptsSummaryKind = 'remainder';
  let summaryAmount: number | null = Math.max(totals.remainder, 0);
  let summaryLabel = 'Остаток';

  if (normalizedStatus === 'архив' || totals.remainder <= 0) {
    summaryKind = 'paid';
    summaryAmount = null;
    summaryLabel = 'Полная оплата';
  } else if (normalizedStatus === 'закрыт') {
    summaryKind = 'debt';
    summaryAmount = totals.remainder;
    summaryLabel = 'Долг';
  } else {
    summaryKind = 'remainder';
    summaryAmount = totals.remainder;
    summaryLabel = 'Остаток';
  }

  return {
    summaryKind,
    summaryAmount,
    summaryLabel,
    tableRows: [...contractRows, totalRow],
    barRows: [...contractRows, totalRow].map((row) => ({
      key: row.key,
      paidPercent: row.paidPercent,
      isTotal: row.isTotal,
      isCurrent: row.isCurrent,
    })),
    totals: {
      ...totals,
      paidPercent,
    },
  };
}
